import { useEffect } from "react";
import { Header } from "@/components/landing/Header";
import { Footer } from "@/components/landing/Footer";
import { UserX, AlertTriangle } from "lucide-react";

const STEPS = [
  "Open the TOTO app and sign in to your account.",
  "Tap the Profile tab in the bottom navigation.",
  "Scroll to the bottom of the Profile screen.",
  "Tap Delete Account.",
  "Read the confirmation message, then tap Delete / Confirm to proceed (or Cancel to stop).",
  "Your account is deleted and you are signed out immediately; associated personal data is removed in line with our privacy policy (subject to any legal retention requirements).",
];

const NOTES = [
  "This action is permanent and cannot be undone — saved contacts, scanned card images, goals, and account history will be removed.",
  "The designated admin/owner account cannot be deleted from the app.",
];

export default function DeleteAccount() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div data-testid="delete-account-page">
      <Header />
      <main className="mx-auto max-w-3xl px-4 pb-24 pt-32 sm:px-6 sm:pt-40">
        <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-toto-lavender text-toto-blue">
          <UserX className="h-6 w-6" />
        </span>
        <h1 className="mt-5 font-heading text-4xl font-bold tracking-tight text-slate-900 sm:text-5xl" data-testid="delete-account-heading">
          How to Delete Your TOTO Account
        </h1>
        <p className="mt-4 text-base leading-relaxed text-slate-600 sm:text-lg">
          You can delete your TOTO account at any time directly from the app. Follow these steps:
        </p>

        <ol className="mt-10 space-y-4" data-testid="delete-account-steps">
          {STEPS.map((step, i) => (
            <li key={i} className="flex items-start gap-4 rounded-2xl border border-slate-100 bg-white p-5 shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-testid={`delete-step-${i + 1}`}>
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-toto-blue font-heading text-sm font-bold text-white">
                {i + 1}
              </span>
              <span className="pt-1 text-base leading-relaxed text-slate-700">{step}</span>
            </li>
          ))}
        </ol>

        <div className="mt-10 rounded-2xl border border-amber-200 bg-amber-50 p-6" data-testid="delete-account-notes">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-600" />
            <h2 className="font-heading text-lg font-semibold text-slate-900">Notes</h2>
          </div>
          <ul className="mt-3 list-disc space-y-2 pl-6 text-base leading-relaxed text-slate-700">
            {NOTES.map((n, i) => (
              <li key={i}>{n}</li>
            ))}
            <li>
              If you cannot access the app, email{" "}
              <a href="mailto:info@kensworth.in" className="font-semibold text-toto-blue hover:underline" data-testid="delete-account-contact-email">
                info@kensworth.in
              </a>{" "}
              to request account deletion.
            </li>
          </ul>
        </div>
      </main>
      <Footer />
    </div>
  );
}
